import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import heroBg from '../assets/hero-bg.jpg';

const HeroContainer = styled.section`
  background-color: #ededed;
  height: 663px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 1200px) {
    height: auto;
    min-height: 663px;
  }
  
  @media (max-width: 479px) {
    height: 620px;
  }
`;

const HeroImage = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 1328px;
  height: 663px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 35px;
  }
  
  @media (max-width: 1200px) {
    width: 100%;
    left: 0;
    transform: none;
  }
  
  @media (max-width: 479px) {
    top: 394px;
    margin-left: 5px;
    width: 98%;
    height: 197px;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  height: 100%;
  z-index: 2;
`;

const BlurBackground = styled.div`
  position: absolute;
  top: 93px;
  left: 30px;
  width: 593px;
  height: 311px;
  background-color: #3a3a3a;
  opacity: 0.55;
  filter: blur(50px);
  border-radius: 0;
  
  @media (max-width: 960px) {
    width: 400px;
  }
  
  @media (max-width: 640px) {
    width: 300px;
  }
  
  @media (max-width: 479px) {
    top: 420px;
    left: 0;
    width: 296px;
    height: 93px;
  }
`;

const NavMenu = styled.nav`
  position: absolute;
  top: 27px;
  left: 30px;
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
  
  a {
    color: white;
    font-size: 18px;
    font-family: 'Geologica', Arial, sans-serif;
    text-decoration: none;
    white-space: nowrap;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  @media (max-width: 1200px) {
    left: 20px;
    gap: 20px;
    
    a {
      font-size: 16px;
    }
  }
  
  @media (max-width: 960px) {
    position: relative;
    top: 20px;
    left: 0;
    justify-content: center;
  }
  
  @media (max-width: 640px) {
    gap: 15px;
    
    a {
      font-size: 14px;
    }
  }
  
  @media (max-width: 479px) {
    display: none;
  }
`;

const Title = styled.h1`
  position: absolute;
  top: 93px;
  left: 30px;
  color: white;
  font-size: 41px;
  font-family: 'Geologica', Arial, sans-serif;
  line-height: 1.35;
  font-weight: 600;
  max-width: 614px;
  
  @media (max-width: 1200px) {
    left: 20px;
    font-size: 36px;
    max-width: 500px;
  }
  
  @media (max-width: 960px) {
    position: relative;
    top: 100px;
    left: 0;
    margin-bottom: 20px;
  }
  
  @media (max-width: 640px) {
    font-size: 28px;
    max-width: 400px;
  }
  
  @media (max-width: 479px) {
    top: 409px;
    left: 0;
    width: 100%;
    max-width: 350px;
    font-size: 22px;
    padding: 0 10px;
  }
`;

const Subtitle = styled.p`
  position: absolute;
  top: 285px;
  left: 30px;
  color: white;
  font-size: 25px;
  font-family: 'Geologica', Arial, sans-serif;
  line-height: 1.35;
  font-weight: 400;
  max-width: 581px;
  
  @media (max-width: 1200px) {
    left: 20px;
    font-size: 22px;
    max-width: 500px;
  }
  
  @media (max-width: 960px) {
    position: relative;
    top: 120px;
    left: 0;
    margin-bottom: 20px;
  }
  
  @media (max-width: 640px) {
    font-size: 18px;
    max-width: 400px;
  }
  
  @media (max-width: 479px) {
    top: 395px;
    left: 0;
    width: 100%;
    max-width: 300px;
    font-size: 13px;
    padding: 0 10px;
  }
`;

const SearchForm = styled.div`
  position: absolute;
  top: 518px;
  left: 21px;
  width: 1159px;
  height: 94px;
  
  @media (max-width: 1200px) {
    width: calc(100% - 40px);
    left: 20px;
  }
  
  @media (max-width: 479px) {
    top: 0;
  }
`;

const Hero = () => {
  const searchRef = useRef(null);
  const scriptLoaded = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const hasReloaded = useRef(false);

  // Проверка на перезагрузку
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('reload') === 'true' && !hasReloaded.current) {
      hasReloaded.current = true;
      
      // Удаляем параметр reload из URL
      searchParams.delete('reload');
      const newSearch = searchParams.toString();
      const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
      
      // Обновляем URL без параметра reload
      navigate(newUrl, { replace: true });
      
      // Перезагружаем страницу
      window.location.reload();
    }
  }, [location, navigate]);

  useEffect(() => {
    let isMounted = true;
    const currentRef = searchRef.current;

    // Функция загрузки скрипта Tourvisor
    const loadTourvisorScript = () => {
      return new Promise((resolve, reject) => {
        if (window.Tourvisor) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://tourvisor.ru/module/init.js';
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    // Функция инициализации виджета
    const initWidget = () => {
      if (window.Tourvisor && currentRef && isMounted) {
        // Удаляем предыдущий виджет если был
        const existingWidget = currentRef.querySelector('.tv-search-form');
        if (existingWidget) {
          existingWidget.innerHTML = '';
        }
        
        // Создаем новый элемент виджета
        const widgetDiv = document.createElement('div');
        widgetDiv.className = 'tv-search-form tv-moduleid-9973493';
        currentRef.appendChild(widgetDiv);
        
        // Переинициализируем Tourvisor
        if (window.Tourvisor.reinit) {
          window.Tourvisor.reinit();
        }
      }
    };

    // Загружаем скрипт и инициализируем виджет
    if (!scriptLoaded.current) {
      scriptLoaded.current = true;
      loadTourvisorScript()
        .then(() => {
          // Даем время на загрузку
          setTimeout(() => {
            if (isMounted) {
              initWidget();
            }
          }, 500);
        })
        .catch(error => {
          console.error('Failed to load Tourvisor script:', error);
        });
    } else {
      initWidget();
    }

    // Очистка при размонтировании
    return () => {
      isMounted = false;
      if (currentRef) {
        const widget = currentRef.querySelector('.tv-search-form');
        if (widget) {
          widget.innerHTML = '';
        }
      }
    };
  }, []);

  return (
    <HeroContainer className="hero-section">
      <HeroImage>
        <img src={heroBg} alt="hero background" />
      </HeroImage>
      
      <ContentWrapper>
        <BlurBackground />
        
        <NavMenu>
          <Link to="/podbor-tyra?reload=true">Подбор тура</Link>
          <Link to="/goryashchie-tury?reload=true">Горящие туры</Link>
          <Link to="/minimalnye-tseny?reload=true">Лечебные туры</Link>
          <Link to="/strany?reload=true">Страны</Link>
          <Link to="/china">Наши туры в Китай</Link>
          <Link to="/cruises">Круизы</Link>
          <Link to="/o-nas">О компании</Link>
          <Link to="/kontakty">Контакты</Link>
        </NavMenu>
        
        <Title>
          Индивидуальные <br />
          и комбинированные туры <br />
          в Китай, Вьетнам и Таиланд
        </Title>
        
        <Subtitle>
          С 2014 года организуем отдых с заботой: перелёты, трансферы, экскурсии <br />
          и сопровождение 24/7
        </Subtitle>
        
        <SearchForm ref={searchRef}>
          <div className="tv-search-form tv-moduleid-9973493"></div>
        </SearchForm>
      </ContentWrapper>
    </HeroContainer>
  );
};

export default Hero;